import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useDelight } from '@/hooks/useDelight';
import { useAuth } from '@/hooks/useAuth';
import { queryKeys } from '@/lib/query-keys';
import { cascadeArchiveClient, cascadeRestoreClient } from '@/lib/archive-cascade';

export interface Client {
  id: string;
  user_id: string;
  name: string;
  health_status: 'red' | 'yellow' | 'green';
  health_notes: string | null;
  created_at: string;
  updated_at: string;
  // Account ownership
  account_owner_id: string | null;
  // ClickUp integration fields
  clickup_list_id: string | null;
  clickup_list_name: string | null;
  clickup_space_id: string | null;
  clickup_space_name: string | null;
  // Internal client flag - tasks from internal meetings can be routed to other client lists
  is_internal: boolean;
  // White-label flag
  is_white_label: boolean;
  // Archived flag - hides client from main views
  is_archived: boolean;
}

export interface ClientGoal {
  id: string;
  client_id: string;
  goal_text: string;
  category: string;
  created_at: string;
}

export interface TeamMember {
  id: string;
  client_id: string;
  name: string;
  role: string;
  created_at: string;
}

export function useClients() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { celebrateGrowth, celebrateHealthBoost } = useDelight();
  const { user } = useAuth();

  // RLS policies enforce ClickUp-based access at the database level
  // Only clients the user has access to will be returned
  const clientsQuery = useQuery({
    queryKey: queryKeys.clients.all,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('clients')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as Client[];
    },
    enabled: !!user?.id,
  });

  const createClient = useMutation({
    mutationFn: async (name: string) => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('clients')
        .insert({ name, user_id: user.id })
        .select()
        .single();

      if (error) throw error;
      return data as Client;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.clients.all });
      toast({ title: 'Client created successfully' });
      celebrateGrowth('client');
    },
    onError: (error) => {
      toast({ title: 'Error creating client', description: error.message, variant: 'destructive' });
    },
  });

  const updateClientHealth = useMutation({
    mutationFn: async ({
      clientId,
      healthStatus,
      healthNotes,
    }: {
      clientId: string;
      healthStatus: 'red' | 'yellow' | 'green';
      healthNotes?: string;
    }) => {
      const { data, error } = await supabase
        .from('clients')
        .update({ health_status: healthStatus, health_notes: healthNotes })
        .eq('id', clientId)
        .select()
        .single();

      if (error) throw error;
      return data as Client;
    },
    onSuccess: (data, variables) => {
      // Invalidate all client-related queries
      queryClient.invalidateQueries({ queryKey: queryKeys.clients.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.clients.detail(data.id) });
      // Also invalidate dashboard widget queries
      queryClient.invalidateQueries({ queryKey: queryKeys.clients.healthWidget });
      queryClient.invalidateQueries({ queryKey: queryKeys.clients.healthContracts });
      toast({ title: 'Client health updated' });
      // Celebrate when health improves to green
      if (variables.healthStatus === 'green') {
        celebrateHealthBoost(data.name || 'Client');
      }
    },
    onError: (error) => {
      toast({ title: 'Error updating health', description: error.message, variant: 'destructive' });
    },
  });

  const updateClientName = useMutation({
    mutationFn: async ({ clientId, name }: { clientId: string; name: string }) => {
      const { data, error } = await supabase
        .from('clients')
        .update({ name })
        .eq('id', clientId)
        .select()
        .single();

      if (error) throw error;
      return data as Client;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.clients.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.clients.detail(data.id) });
      toast({ title: 'Client name updated' });
    },
    onError: (error) => {
      toast({
        title: 'Error updating client name',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const deleteClient = useMutation({
    mutationFn: async (clientId: string) => {
      const { error } = await supabase.from('clients').delete().eq('id', clientId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.clients.all });
      toast({ title: 'Client deleted' });
    },
    onError: (error) => {
      toast({ title: 'Error deleting client', description: error.message, variant: 'destructive' });
    },
  });

  const updateClientInternal = useMutation({
    mutationFn: async ({ clientId, isInternal }: { clientId: string; isInternal: boolean }) => {
      const { data, error } = await supabase
        .from('clients')
        .update({ is_internal: isInternal })
        .eq('id', clientId)
        .select()
        .single();

      if (error) throw error;
      return data as Client;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.clients.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.clients.detail(data.id) });
      toast({
        title: data.is_internal ? 'Marked as internal client' : 'Marked as external client',
      });
    },
    onError: (error) => {
      toast({ title: 'Error updating client', description: error.message, variant: 'destructive' });
    },
  });

  const updateClientWhiteLabel = useMutation({
    mutationFn: async ({ clientId, isWhiteLabel }: { clientId: string; isWhiteLabel: boolean }) => {
      const { data, error } = await supabase
        .from('clients')
        .update({ is_white_label: isWhiteLabel })
        .eq('id', clientId)
        .select()
        .single();

      if (error) throw error;
      return data as Client;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.clients.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.clients.detail(data.id) });
      toast({
        title: data.is_white_label ? 'Marked as white-label client' : 'Marked as standard client',
        description: data.is_white_label
          ? 'Summaries will be sanitized and direct email is disabled.'
          : 'Standard client settings restored.',
      });
    },
    onError: (error) => {
      toast({ title: 'Error updating client', description: error.message, variant: 'destructive' });
    },
  });

  const updateClientArchived = useMutation({
    mutationFn: async ({ clientId, isArchived }: { clientId: string; isArchived: boolean }) => {
      // 1. Update the client first
      const { data, error } = await supabase
        .from('clients')
        .update({ is_archived: isArchived })
        .eq('id', clientId)
        .select()
        .single();

      if (error) throw error;

      // 2. Cascade to related entities (best-effort - log errors but don't fail)
      if (isArchived) {
        await cascadeArchiveClient(clientId);
      } else {
        await cascadeRestoreClient(clientId);
      }

      return data as Client;
    },
    onSuccess: (data) => {
      // Invalidate client queries
      queryClient.invalidateQueries({ queryKey: queryKeys.clients.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.clients.detail(data.id) });
      // Invalidate dashboard widget queries
      queryClient.invalidateQueries({ queryKey: queryKeys.clients.healthWidget });
      queryClient.invalidateQueries({ queryKey: queryKeys.clients.healthContracts });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.goalsHealthWidgetData });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.complianceWidgetData });
      // Invalidate related entity queries
      queryClient.invalidateQueries({ queryKey: queryKeys.contracts.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.contracts.byClient(data.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.agreements.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.agreements.list({ clientId: data.id }) });
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.byClient(data.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.projectsV2.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.projectsV2.byClient(data.id) });
      // Invalidate budget queries so financial metrics reflect the change
      queryClient.invalidateQueries({ queryKey: queryKeys.budget.dashboard });
      queryClient.invalidateQueries({ queryKey: queryKeys.budget.metrics });

      toast({
        title: data.is_archived ? 'Client archived' : 'Client restored',
        description: data.is_archived
          ? 'Client and related contracts, agreements, and projects have been archived.'
          : 'Client and related items have been restored to the active list.',
      });
    },
    onError: (error) => {
      toast({ title: 'Error updating client', description: error.message, variant: 'destructive' });
    },
  });

  return {
    clients: clientsQuery.data ?? [],
    isLoading: clientsQuery.isLoading,
    error: clientsQuery.error,
    createClient,
    updateClientHealth,
    updateClientName,
    deleteClient,
    updateClientInternal,
    updateClientWhiteLabel,
    updateClientArchived,
  };
}

// Re-export useClientDetails from its dedicated file for backward compatibility
export { useClientDetails } from './useClientDetails';
